import React, { useContext, useCallback, useMemo } from "react";
import _ from "lodash";
import { BreakpointTrackerContext } from "@jutro/layout";
import { WizardPage, wizardProps } from "@xengage/gw-portals-wizard-react";
import { ViewModelForm, withViewModelService } from "@xengage/gw-portals-viewmodel-react";
import { useValidation } from "@xengage/gw-portals-validation-react";
import { readViewModelValue } from "@xengage/gw-jutro-adapters-react";
import { EntityUtil } from '@xengage/gw-portals-util-js';
import { PremierConstants } from "premier-constant-service";
import { useUpdateSubmissionServices, validationUtil } from "premier-capability-hooks";
import metadata from "./CondominiumsPage.metadata.json5";
import styles from "../../SpecialtyPersonalStyles.module.scss"
import CondominiumsDetails from "../../components/Condominiums/CondominiumsDetails/CondominiumsDetails";

function CondominiumsPage(props) {
  const {
    wizardData: submissionVM,
    updateWizardData,
    viewModelService
  } = props;
  const breakpoint = useContext(BreakpointTrackerContext);
  const { onValidate, initialValidation } = useValidation(
    "CondominiumsPage"
  );
  const { updateSubmission } = useUpdateSubmissionServices();
  const { lineName } = PremierConstants;

  const onNext = useCallback(async () => {
    const productCode = _.get(submissionVM.value, "baseData.productCode");
    // eslint-disable-next-line security/detect-object-injection
    const lobLineName = productCode ? lineName[productCode] : "";
    const response = await updateSubmission(
      lobLineName,
      "CondominiumsPage",
      submissionVM
    );

    submissionVM.value = response;

    return submissionVM;
  }, [lineName, submissionVM, updateSubmission]);

  const addCondominiums = useCallback(async () => {

    const condominiumsObj = {
      tempID: EntityUtil.nextId(),
      propertyDetails: {
        isValidationReq: true
      },
      construction: {
        isValidationReq: true
      },
      other: {
        isValidationReq: true
      }
    };

    const { _xCenter, _dtoName } = submissionVM.lobData.perLine.coverables.condoCoverable.condominiums;

    const condominiumsVM = viewModelService.create(condominiumsObj, _xCenter, _dtoName);

    submissionVM.lobData.perLine.coverables.condoCoverable.condominiums.value = submissionVM.lobData.perLine.coverables.condoCoverable.condominiums.value || []


    submissionVM.lobData.perLine.coverables.condoCoverable.condominiums.pushElement(
      condominiumsVM
    );
    updateWizardData(submissionVM);
  },
    [submissionVM, viewModelService, updateWizardData]
  );

  const condominiumsOverrides = useMemo(() => {
    const condominiumsArray = _.get(submissionVM, 'lobData.perLine.coverables.condoCoverable.condominiums', []);
    let overrides = [];

    if (!_.isEmpty(condominiumsArray)) {
      overrides = condominiumsArray?.children.map((condominiums, index) => ({
        [`condominiumsDetailsComponent${index}`]: {
          overrideProps: {
            "@field": {
              // apply to all fields
              showRequired: true,
              labelPosition: "left",
            },
          },
          submissionVM,
          updateWizardData,
          pathUpdated: `lobData.perLine.coverables.condoCoverable.condominiums.children[${index}]`,
          condominiumsVm: condominiums,
          index,
          viewModelService
        }
      }));
    }

    return Object.assign({}, ...overrides);
  }, [submissionVM, updateWizardData, viewModelService]);


  const onSave = useCallback(
    async () => {
      const subVMCopy = _.clone(submissionVM)
      const productCode = _.get(subVMCopy.value, 'baseData.productCode');

      validationUtil.unSetIsValidationReqFlag(subVMCopy.lobData.perLine.coverables.condoCoverable.condominiums.value, ' ')

      const lobLineName = productCode ? lineName[productCode] : '';
      await updateSubmission(lobLineName, 'CondominiumsPage', subVMCopy);
                      
      return submissionVM;
    },
    [lineName, submissionVM, updateSubmission]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const overrideProps = {
    "@field": {
      showRequired: true,
      labelPosition: breakpoint === "desktop" ? "left" : "top",
    },
    ...condominiumsOverrides,
  };

  const resolvers = {
    resolveCallbackMap: {
      onValidate,
      addCondominiums,
    },
    resolveComponentMap: {
      condominiumsDetailsComponent: CondominiumsDetails
    },
    resolveClassNameMap: styles
  };

  const readValue = useCallback(
    (id, path) => readViewModelValue(metadata.pageContent, submissionVM, id, path, overrideProps),
    [submissionVM, overrideProps]
  );

  const isDisableNext = () =>
    !submissionVM.lobData.perLine.coverables.condoCoverable.condominiums
      .aspects.subtreeValid;

  return (
    <WizardPage
      onNext={onNext}
      onSave={onSave}
      skipWhen={initialValidation}
      disableNext={isDisableNext()}
    >
      <ViewModelForm
        uiProps={metadata.pageContent}
        model={submissionVM}
        onModelChange={updateWizardData}
        overrideProps={overrideProps}
        onValidationChange={onValidate}
        resolveValue={readValue}
        callbackMap={resolvers.resolveCallbackMap}
        componentMap={resolvers.resolveComponentMap}
        classNameMap={resolvers.resolveClassNameMap}
      />
    </WizardPage>
  );
}

CondominiumsPage.propTypes = wizardProps;
export default withViewModelService(CondominiumsPage);

